import React, { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import LinearProgress from '@mui/material/LinearProgress'
import { OpenNewFieldIcon } from '@sales-center/components'
import { ENTITY_TYPES } from '@sales-center/common'
import { loadEntityLocations } from '@user/store/entityLocations'
import { useAppDispatch } from '@user/store/hooks'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import {
  MatchCreateLocationDialogs,
  useMatchCreateLocationFlow,
} from '@user/components/Location/MatchCreateLocation'

type Props = {
  entityId: number
  entityType: ENTITY_TYPES
  onSaveSuccess: () => void
  onSavingChange: (saving: boolean) => void
}

export function PestPacLocationActions(props: Props) {
  const { entityId, entityType, onSaveSuccess, onSavingChange } = props
  const dispatch = useAppDispatch()
  const tidalThemeActive = useTidalThemeActive()

  const flow = useMatchCreateLocationFlow({
    entityId,
    entityType,
    onSaveSuccess,
  })
  const { canUpdateEntity, saving } = flow

  const [locationsLoading, setLocationsLoading] = useState(true)
  const [locationsLoadFailed, setLocationsLoadFailed] = useState(false)

  useEffect(() => {
    onSavingChange(saving)
  }, [onSavingChange, saving])

  useEffect(() => () => onSavingChange(false), [onSavingChange])

  useEffect(() => {
    let active = true
    if (!canUpdateEntity) {
      setLocationsLoading(false)
      return
    }
    setLocationsLoading(true)
    setLocationsLoadFailed(false)
    dispatch(loadEntityLocations({ entityId, entityType })).then((action) => {
      if (!active) return
      if (loadEntityLocations.rejected.match(action)) {
        setLocationsLoadFailed(true)
      }
      setLocationsLoading(false)
    })
    return () => {
      active = false
    }
  }, [canUpdateEntity, dispatch, entityId, entityType])

  if (!canUpdateEntity) return null

  return (
    <>
      {locationsLoading ? (
        <Box display={'flex'} justifyContent={'center'} py={4}>
          <CircularProgress />
        </Box>
      ) : locationsLoadFailed ? (
        <Box pt={2}>
          <Alert severity="warning">
            Unable to load location details. Please close this dialog and try
            again.
          </Alert>
        </Box>
      ) : (
        <>
          <Box
            pt={2}
            display={'flex'}
            flexDirection={'row'}
            flexWrap={'wrap'}
            gap={2}
            alignItems={'center'}
          >
            <Button
              variant="outlined"
              data-test-id="map-view-create-location-button"
              onClick={flow.handleCreateNewLocation}
              disabled={saving}
            >
              Create Location
            </Button>
            <Button
              variant="outlined"
              data-test-id="map-view-match-location-button"
              onClick={flow.handleMatchLocation}
              disabled={saving}
              endIcon={<OpenNewFieldIcon />}
            >
              Match to Existing PestPac Account
            </Button>
          </Box>
          {saving && <LinearProgress color="primary" sx={{ width: '100%' }} />}
        </>
      )}
      <MatchCreateLocationDialogs
        entityId={entityId}
        entityType={entityType}
        flow={flow}
        tidalThemeActive={tidalThemeActive}
        addToPestPac={true}
      />
    </>
  )
}
PestPacLocationActions.displayName = 'PestPacLocationActions'
